import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  BookOpen, 
  Calendar, 
  Home, 
  Save,
  X,
  Upload,
  Loader2,
  Video,
  RotateCcw,
  HelpCircle,
  Plus
} from 'lucide-react'
import { CourseForm } from '../types'
import coursesService from '../services/coursesService'
import {
  MEDIA_SECTIONS,
  validateImageFiles,
  validateVideoFiles,
} from '../config/mediaValidation'
import {
  validateCourse,
  validateCourseStep1,
  validateCourseStep2,
  getCourseValidationStep,
} from '../validation'
import CourseDateRangesEditor from '../components/CourseDateRangesEditor'
import {
  PRESET_YOGA_STYLES,
  mergeYogaStyles,
  parseCustomYogaStylesInput,
} from '../constants/yogaStyles'
import {
  createEmptyDateRangeRow,
  formatDateRangesSummary,
  normalizeDateRangeRows,
  parseDateRangesFromCourse,
  type CourseDateRangeRow,
} from '../utils/courseSchedule'
import roomService, { Room } from '../services/roomService'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import RoomTypesManageModal from '../components/RoomTypesManageModal'

const STORAGE_KEY = 'course_form_draft'
const PENDING_MEDIA_KEY = 'course_pending_media'

// Helper to save pending media files to sessionStorage
const savePendingMediaToStorage = async (pendingFiles: Array<{ file: File; type: 'image' | 'video'; category: 'media' | 'description'; previewUrl?: string; uploadStatus?: 'pending' | 'uploading' | 'success' | 'failed'; dataUrl?: string }>) => {
  try {
    const filesToSave = await Promise.all(
      pendingFiles.map(async (item) => {
        // If dataUrl already exists, use it; otherwise convert file to data URL
        if (item.dataUrl) {
          return {
            dataUrl: item.dataUrl,
            type: item.type,
            category: item.category,
            uploadStatus: item.uploadStatus || 'pending',
            fileName: item.file.name,
            fileType: item.file.type,
            fileSize: item.file.size
          }
        } else {
          return new Promise<{ dataUrl: string; type: string; category: string; uploadStatus: string; fileName: string; fileType: string; fileSize: number }>((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => {
              resolve({
                dataUrl: reader.result as string,
                type: item.type,
                category: item.category,
                uploadStatus: item.uploadStatus || 'pending',
                fileName: item.file.name,
                fileType: item.file.type,
                fileSize: item.file.size
              })
            }
            reader.readAsDataURL(item.file)
          })
        }
      })
    )
    sessionStorage.setItem(PENDING_MEDIA_KEY, JSON.stringify(filesToSave))
  } catch (error) {
    console.error('Error saving pending media to storage:', error)
  }
}

// Helper to restore pending media files from sessionStorage
const restorePendingMediaFromStorage = async (): Promise<Array<{ file: File; type: 'image' | 'video'; category: 'media' | 'description'; previewUrl: string; uploadStatus: 'pending' | 'uploading' | 'success' | 'failed'; dataUrl: string }>> => {
  try {
    const stored = sessionStorage.getItem(PENDING_MEDIA_KEY)
    if (!stored) return []
    
    const pendingMediaData = JSON.parse(stored)
    const restoredFiles = await Promise.all(
      pendingMediaData.map(async (item: { dataUrl: string; type: 'image' | 'video'; category: 'media' | 'description'; uploadStatus?: string; fileName?: string; fileType?: string; fileSize?: number }) => {
        try {
          // Convert data URL to blob, then to File
          const response = await fetch(item.dataUrl)
          const blob = await response.blob()
          const file = new File(
            [blob], 
            item.fileName || `restored-${Date.now()}.${item.type === 'image' ? 'jpg' : 'mp4'}`,
            { type: item.fileType || blob.type }
          )
          const previewUrl = URL.createObjectURL(file)
          return {
            file,
            type: item.type,
            category: item.category,
            previewUrl,
            uploadStatus: (item.uploadStatus || 'pending') as 'pending' | 'uploading' | 'success' | 'failed',
            dataUrl: item.dataUrl
          }
        } catch (err) {
          console.error('Error restoring pending media file:', err)
          return null
        }
      })
    )
    return restoredFiles.filter(Boolean) as Array<{ file: File; type: 'image' | 'video'; category: 'media' | 'description'; previewUrl: string; uploadStatus: 'pending' | 'uploading' | 'success' | 'failed'; dataUrl: string }>
  } catch (error) {
    console.error('Error restoring pending media from storage:', error)
    return []
  }
}

const ALLOWED_COURSE_CURRENCIES = ['USD', 'EUR', 'INR', 'IDR'] as const

function normalizeCourseCurrency(code: string | undefined): string {
  const c = String(code || 'USD').toUpperCase()
  return (ALLOWED_COURSE_CURRENCIES as readonly string[]).includes(c) ? c : 'USD'
}

function deriveOffersAccommodationFromDraft(parsed: {
  accommodation?: { available?: boolean; rooms?: unknown[] }
  price?: number
  bookingAmount?: number
}): boolean {
  if (parsed.accommodation?.available) return true
  if ((parsed.accommodation?.rooms?.length ?? 0) > 0) return true
  if (
    parsed.accommodation?.available === false &&
    ((parsed.price != null && parsed.price > 0) || parsed.bookingAmount != null)
  ) {
    return false
  }
  return true
}

const CreateCourse: React.FC = () => {
  const navigate = useNavigate()
  const { courseId } = useParams<{ courseId?: string }>()
  const { user, selectedSchool, schools, loading: authLoading } = useAuth()
  const isEditMode = !!courseId
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadingCourse, setLoadingCourse] = useState(false)
  const [dateRangeRows, setDateRangeRows] = useState<CourseDateRangeRow[]>(() => [
    createEmptyDateRangeRow(),
  ])
  const [saveVersion, setSaveVersion] = useState(0) // Increment to trigger save
  const [schoolRooms, setSchoolRooms] = useState<Room[]>([])
  const [loadingRooms, setLoadingRooms] = useState(false)
  const [roomTypesModalOpen, setRoomTypesModalOpen] = useState(false)
  /** Step 3: whether the school offers accommodation for this course (default: yes) */
  const [offersAccommodation, setOffersAccommodation] = useState(true)
  /** Show inline step errors only after the user tries to leave that step */
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(() => new Set())
  const [customYogaStyleInput, setCustomYogaStyleInput] = useState('')
  const [uploadingMedia, setUploadingMedia] = useState(false)
  const [pendingMediaFiles, setPendingMediaFiles] = useState<Array<{ file: File; type: 'image' | 'video'; category: 'media' | 'description'; previewUrl?: string; uploadStatus?: 'pending' | 'uploading' | 'success' | 'failed'; dataUrl?: string }>>([])
  const [mediaUrls, setMediaUrls] = useState<{ images: string[]; videos: string[] }>({ images: [], videos: [] })
  const isInitializing = useRef(true)
  const prevFormDataRef = useRef<string>('')
  const prevActiveMonthsRef = useRef<string>('')
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState<CourseForm & { startDay?: number; activeMonths?: number[] }>({
    schoolId: selectedSchool?._id || '',
    name: '',
    type: 'TRAINING',
    description: '',
    shortDescription: '',
    price: undefined,
    bookingAmount: undefined,
    currency: 'USD',
    duration: 1,
    maxStudents: undefined,
    minStudents: undefined,
    difficulty: undefined,
    yogaStyles: [],
    schedule: {
      startDate: '',
      endDate: '',
      timeSlots: []
    },
    startDay: 1,
    activeMonths: [],
    inclusions: [],
    requirements: [],
    whoIsThisFor: [],
    courseHighlights: [],
    whatYouWillGain: [],
    curriculum: [],
    faq: [],
    media: {
      images: [],
      videos: []
    },
    accommodation: {
      available: true,
      rooms: [],
      mealPlan: {
        included: false,
        type: 'VEGETARIAN'
      }
    },
    images: [],
    yogaAllianceCertified: false,
    isActive: false,
    isFeatured: false
  })

  // Refs to store latest values for saving
  const formDataRef = useRef(formData)
  const dateRangeRowsRef = useRef(dateRangeRows)
  
  // Store raw textarea values to preserve newlines while typing
  const [textareaValues, setTextareaValues] = useState<{ [key: string]: string }>({})

  // Load course data if in edit mode, otherwise load from sessionStorage
  useEffect(() => {
    if (isEditMode && courseId) {
      fetchCourseData()
    } else {
      // Load from sessionStorage on mount (only for create mode)
      const loadDraftData = async () => {
        try {
          // Always clear mediaUrls when starting a new course
          setMediaUrls({ images: [], videos: [] })
          
          const stored = sessionStorage.getItem(STORAGE_KEY)
          if (stored) {
            const parsed = JSON.parse(stored)
            
            // Check if the draft belongs to the current school
            const draftSchoolId = parsed.schoolId
            const currentSchoolId = selectedSchool?._id
            
            // If school IDs don't match, clear the draft's media to prevent showing images from previous courses
            if (draftSchoolId !== currentSchoolId) {
              console.log('Draft belongs to different school, clearing media', {
                draftSchoolId,
                currentSchoolId
              })
              parsed.media = { images: [], videos: [] }
              // Also clear pending media
              setPendingMediaFiles([])
              sessionStorage.removeItem(PENDING_MEDIA_KEY)
            }
            
            if (parsed.dateRangeRows && Array.isArray(parsed.dateRangeRows) && parsed.dateRangeRows.length > 0) {
              setDateRangeRows(parsed.dateRangeRows)
              prevActiveMonthsRef.current = JSON.stringify(parsed.dateRangeRows)
            }
            prevFormDataRef.current = stored
            // Clean up blob URLs from media if any exist
            if (parsed.media) {
              parsed.media = {
                images: (parsed.media.images || []).filter((key: string) => 
                  key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
                ),
                videos: (parsed.media.videos || []).filter((key: string) => 
                  key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
                )
              }
            }
            
            const offersFromDraft = deriveOffersAccommodationFromDraft(parsed)
            // Update formData with loaded values (keep accommodation.available in sync with offers choice)
            setFormData(prev => ({
              ...prev,
              ...parsed,
              currency: normalizeCourseCurrency(parsed.currency),
              activeMonths: undefined, // Don't set activeMonths in formData, it's separate state
              accommodation: {
                available: offersFromDraft,
                rooms: offersFromDraft ? (parsed.accommodation?.rooms || []) : [],
                mealPlan: parsed.accommodation?.mealPlan || {
                  included: false,
                  type: 'VEGETARIAN',
                },
              },
            }))
            setOffersAccommodation(offersFromDraft)

            // Only restore pending media if draft belongs to current school
            if (draftSchoolId === currentSchoolId) {
              const restoredPendingFiles = await restorePendingMediaFromStorage()
              if (restoredPendingFiles.length > 0) {
                setPendingMediaFiles(restoredPendingFiles)
              }
            }
          } else {
            // No stored draft - ensure media is empty
            setFormData(prev => ({
              ...prev,
              media: { images: [], videos: [] }
            }))
            setPendingMediaFiles([])
            sessionStorage.removeItem(PENDING_MEDIA_KEY)
          }
        } catch (error) {
          console.error('Error loading draft from storage:', error)
          // On error, ensure clean state
          setMediaUrls({ images: [], videos: [] })
          setPendingMediaFiles([])
        }
      }
      
      loadDraftData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, courseId, selectedSchool?._id]) // Only run once on mount or when courseId/school changes

  // Log auth state changes
  useEffect(() => {
    console.log('CreateCourse: Auth state changed', {
      authLoading,
      schools: schools?.length || 0,
      schoolsIsNull: schools === null,
      schoolsIsArray: Array.isArray(schools),
      selectedSchool: selectedSchool?._id,
      selectedSchoolName: selectedSchool?.name,
      user: user?._id,
      userRole: user?.role
    })
  }, [authLoading, schools, selectedSchool, user])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      pendingMediaFiles.forEach(({ previewUrl }) => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl)
        }
      })
    }
  }, [pendingMediaFiles])

  // Convert fileKeys to signed URLs for display
  useEffect(() => {
    const convertMediaToUrls = async () => {
      const imageFileKeys = formData.media?.images || []
      const videoFileKeys = formData.media?.videos || []
      
      // Filter out blob URLs (these are temporary previews, not actual fileKeys)
      const validImageFileKeys = imageFileKeys.filter(key => 
        key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
      )
      const validVideoFileKeys = videoFileKeys.filter(key => 
        key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
      )
      
      // Add pending media preview URLs for display (these are blob URLs for files waiting to be uploaded)
      // Only show previews for files that haven't failed upload
      const pendingImagePreviews = pendingMediaFiles
        .filter(item => 
          item.type === 'image' && 
          item.category === 'media' && 
          item.previewUrl &&
          item.uploadStatus !== 'failed'
        )
        .map(item => item.previewUrl!)
      const pendingVideoPreviews = pendingMediaFiles
        .filter(item => 
          item.type === 'video' && 
          item.category === 'media' && 
          item.previewUrl &&
          item.uploadStatus !== 'failed'
        )
        .map(item => item.previewUrl!)
      
      console.log('Media conversion:', {
        pendingImagePreviews,
        pendingVideoPreviews,
        pendingMediaFilesCount: pendingMediaFiles.length,
        validImageFileKeys,
        validVideoFileKeys
      })
      
      // If we have pending previews but no fileKeys, show previews immediately
      if ((pendingImagePreviews.length > 0 || pendingVideoPreviews.length > 0) && validImageFileKeys.length === 0 && validVideoFileKeys.length === 0) {
        console.log('Setting mediaUrls to pending previews only')
        setMediaUrls({ 
          images: pendingImagePreviews, 
          videos: pendingVideoPreviews 
        })
        return
      }
      
      // Combine all fileKeys
      const allFileKeys = [...validImageFileKeys, ...validVideoFileKeys].filter(Boolean)
      
      if (allFileKeys.length === 0 && pendingImagePreviews.length === 0 && pendingVideoPreviews.length === 0) {
        setMediaUrls({ images: [], videos: [] })
        return
      }
      
      try {
        const mediaService = (await import('../services/mediaService')).default
        
        // Filter out URLs that are already full URLs (but keep blob URLs for pending uploads)
        const keysToFetch = allFileKeys.filter(key => 
          key && !key.startsWith('http://') && !key.startsWith('https://') && !key.startsWith('blob:')
        )
        
        const urlMap = new Map<string, string>()
        keysToFetch.forEach((key) => {
          urlMap.set(key, mediaService.getMediaUrlFromKey(key))
        })
        
        const getUrl = (key: string): string => {
          if (!key) return ''
          if (key.startsWith('blob:') || key.startsWith('http://localhost')) return ''
          if (key.startsWith('http://') || key.startsWith('https://')) return key
          return urlMap.get(key) || mediaService.getMediaUrlFromKey(key) || ''
        }
        
        // Convert images (combine uploaded fileKeys with pending previews)
        // Keep one entry per key so display order/count matches data (use placeholder if URL fails to resolve)
        const imageUrls = [
          ...validImageFileKeys.map(key => {
            const url = getUrl(key)
            return (url && url.length > 0) ? url : 'https://via.placeholder.com/96'
          }),
          ...pendingImagePreviews
        ]
        
        // Convert videos (same: one entry per key so indices stay in sync)
        const videoUrls = [
          ...validVideoFileKeys.map(key => {
            const url = getUrl(key)
            return (url && url.length > 0) ? url : ''
          }),
          ...pendingVideoPreviews
        ]
        
        setMediaUrls({ images: imageUrls, videos: videoUrls })
      } catch (error) {
        console.error('Error converting media to URLs:', error)
        // Fallback to original values
        setMediaUrls({ 
          images: imageFileKeys, 
          videos: videoFileKeys 
        })
      }
    }
    
    convertMediaToUrls()
  }, [formData.media?.images, formData.media?.videos, pendingMediaFiles])

  const fetchCourseData = async () => {
    if (!courseId) return
    
    setLoadingCourse(true)
    try {
      const response = await coursesService.getCourseById(courseId)
      if (response.success) {
        // Backend returns { success: true, data: { course: Course } }
        const course = (response.data as any).course || response.data
        
        // Populate form with course data
        setFormData({
          schoolId: course.schoolId?._id || course.schoolId || selectedSchool?._id || '',
          name: course.name || '',
          type: course.type || 'TRAINING',
          description: course.description || '',
          shortDescription: course.shortDescription || '',
          price: course.price,
          bookingAmount: course.bookingAmount,
          currency: normalizeCourseCurrency(course.currency),
          duration: course.duration || 1,
          maxStudents: course.maxStudents,
          minStudents: course.minStudents,
          difficulty: course.difficulty,
          yogaStyles: course.yogaStyles || [],
          schedule: {
            startDate: course.schedule?.startDate ? new Date(course.schedule.startDate).toISOString().split('T')[0] : '',
            endDate: course.schedule?.endDate ? new Date(course.schedule.endDate).toISOString().split('T')[0] : '',
            timeSlots: (course.schedule?.timeSlots || []).map((ts: any) => ({
              label: ts.label || '',
              startTime: ts.startTime || '',
              endTime: ts.endTime || ''
            }))
          },
          startDay: course.startDay || course.schedule?.startDay || 1,
          activeMonths: course.activeMonths || course.schedule?.activeMonths || [],
          inclusions: course.inclusions || [],
          requirements: course.requirements || [],
          whoIsThisFor: course.whoIsThisFor || [],
          courseHighlights: course.courseHighlights || [],
          whatYouWillGain: course.whatYouWillGain || [],
          curriculum: course.curriculum || [],
          faq: course.faq || [],
          media: course.media ? {
            images: (course.media.images || []).filter((key: string) => 
              key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
            ),
            videos: (course.media.videos || []).filter((key: string) => 
              key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
            )
          } : { images: [], videos: [] },
          accommodation: course.accommodation || {
            available: false,
            rooms: [],
            mealPlan: {
              included: false,
              type: 'VEGETARIAN'
            }
          },
          images: course.images || [],
          yogaAllianceCertified: course.yogaAllianceCertified ?? false,
          isActive: course.isActive !== undefined ? course.isActive : false,
          isFeatured: course.isFeatured !== undefined ? course.isFeatured : false
        })
        
        setDateRangeRows(
          parseDateRangesFromCourse(course.schedule, course.duration || 1),
        )
        setOffersAccommodation(Boolean(course.accommodation?.available))
      }
    } catch (error) {
      console.error('Error fetching course data:', error)
      toast.error('Failed to load course data')
      navigate('/courses')
    } finally {
      setLoadingCourse(false)
    }
  }

  const steps = [
    { id: 1, title: 'Basic Info', icon: BookOpen, description: 'Course details and pricing' },
    { id: 2, title: 'Schedule', icon: Calendar, description: 'Dates and timing' },
    { id: 3, title: 'Accommodation', icon: Home, description: 'Room and meal options' },
    { id: 4, title: 'Review', icon: Check, description: isEditMode ? 'Review and update' : 'Review and create' }
  ]


  // Load activeMonths from storage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed.dateRangeRows?.length) {
          setDateRangeRows(parsed.dateRangeRows)
        }
      }
    } catch (error) {
      console.error('Error loading schedule draft from storage:', error)
    }
  }, [])

  // Mark initialization complete after first render cycle
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitializing.current = false
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Debounced save function
  const debouncedSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  
  // Save function that uses refs to get latest values (only for create mode)
  const performSave = React.useCallback(() => {
    // Don't auto-save in edit mode
    if (isEditMode || isInitializing.current) {
      return
    }

    if (debouncedSaveRef.current) {
      clearTimeout(debouncedSaveRef.current)
    }

    debouncedSaveRef.current = setTimeout(() => {
      try {
        // Use refs to get latest values
        const dataToSave = {
          ...formDataRef.current,
          dateRangeRows: dateRangeRowsRef.current,
        }
        
        // Clean up blob URLs from media before saving
        if (dataToSave.media) {
          dataToSave.media = {
            images: (dataToSave.media.images || []).filter((key: string) => 
              key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
            ),
            videos: (dataToSave.media.videos || []).filter((key: string) => 
              key && !key.startsWith('blob:') && !key.startsWith('http://localhost')
            )
          }
        }
        
        const dataString = JSON.stringify(dataToSave)
        const monthsString = JSON.stringify(dateRangeRowsRef.current)
        
        // Only save if data actually changed
        if (prevFormDataRef.current !== dataString || prevActiveMonthsRef.current !== monthsString) {
          sessionStorage.setItem(STORAGE_KEY, dataString)
          prevFormDataRef.current = dataString
          prevActiveMonthsRef.current = monthsString
        }
      } catch (error) {
        console.error('Error saving draft to storage:', error)
      }
    }, 500)
  }, [isEditMode])

  // Update refs and trigger save when saveVersion changes
  React.useEffect(() => {
    if (saveVersion === 0 || isInitializing.current) {
      return
    }
    
    // Update refs with latest values (captured from current render)
    formDataRef.current = formData
    dateRangeRowsRef.current = dateRangeRows
    
    // Trigger save
    performSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveVersion]) // Only depend on saveVersion - formData/activeMonths are from current render

  // Fetch rooms when school is selected
  const fetchSchoolRooms = async (schoolId: string) => {
    if (!schoolId) return
    
    setLoadingRooms(true)
    try {
      const rooms = await roomService.getSchoolRooms(schoolId, true) // Only fetch active rooms
      setSchoolRooms(rooms)
    } catch (error) {
      console.error('Error fetching rooms:', error)
      // Don't show error toast, just log it - rooms might not be set up yet
    } finally {
      setLoadingRooms(false)
    }
  }

  useEffect(() => {
    if (isInitializing.current) {
      return
    }
    
    // Wait for auth to finish loading
    if (authLoading) {
      return
    }
    
    if (!selectedSchool) {
      // No school selected - don't auto-select, let user select manually
      // Check if schools are still loading
      if (schools === null) {
        // Schools still loading, don't do anything yet
        return
      }
      // If no selectedSchool, don't set formData.schoolId automatically
      // User must select a school or it will be set from formData
    } else {
      setFormData(prev => {
        if (prev.schoolId === selectedSchool._id) return prev
        return { ...prev, schoolId: selectedSchool._id }
      })
      fetchSchoolRooms(selectedSchool._id)
    }
  }, [selectedSchool, schools, navigate, authLoading, user])

  // Fetch rooms when accommodation is enabled
  useEffect(() => {
    if (offersAccommodation && formData.schoolId && (!schoolRooms || schoolRooms.length === 0)) {
      fetchSchoolRooms(formData.schoolId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersAccommodation, formData.schoolId])

  // When user returns to this tab (e.g. after adding rooms in another tab), refetch rooms so dropdown updates
  useEffect(() => {
    if (!formData.schoolId) return
    const onFocus = () => {
      if (offersAccommodation) {
        fetchSchoolRooms(formData.schoolId)
      }
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.schoolId, offersAccommodation])


  const triggerSave = () => {
    setSaveVersion(prev => prev + 1)
  }

  const addCustomYogaStylesFromInput = () => {
    const parsed = parseCustomYogaStylesInput(customYogaStyleInput)
    if (parsed.length === 0) return
    const current = formData.yogaStyles || []
    handleInputChange('yogaStyles', mergeYogaStyles(current, parsed))
    setCustomYogaStyleInput('')
  }

  const handleCustomYogaStyleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addCustomYogaStylesFromInput()
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    triggerSave()
  }

  const setAccommodationChoice = (offers: boolean) => {
    setOffersAccommodation(offers)
    clearStepAttempted(3)
    setFormData((prev) => ({
      ...prev,
      ...(offers
        ? { price: undefined, bookingAmount: undefined }
        : {}),
      accommodation: {
        available: offers,
        rooms: offers ? (prev.accommodation?.rooms || []) : [],
        mealPlan: offers
          ? prev.accommodation?.mealPlan || { included: false, type: 'VEGETARIAN' }
          : { included: false, type: 'VEGETARIAN' },
      },
    }))
    triggerSave()
  }

  const handleNestedChange = (parent: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof CourseForm] as any),
        [field]: value
      }
    }))
    triggerSave()
  }

  const handleArrayAdd = (field: string, item: any) => {
    setFormData(prev => {
      // Handle nested paths like 'accommodation.rooms'
      if (field.includes('.')) {
        const [parent, child] = field.split('.')
        const parentValue = prev[parent as keyof CourseForm] as any
        const currentArray = parentValue?.[child] || []
        return {
          ...prev,
          [parent]: {
            ...parentValue,
            [child]: [...(Array.isArray(currentArray) ? currentArray : []), item]
          }
        }
      }
      // Handle direct array fields
      const currentArray = prev[field as keyof CourseForm] as any[]
      return {
        ...prev,
        [field]: [...(Array.isArray(currentArray) ? currentArray : []), item]
      }
    })
    triggerSave()
  }

  const handleArrayRemove = (field: string, index: number) => {
    setFormData(prev => {
      // Handle nested paths like 'accommodation.rooms'
      if (field.includes('.')) {
        const [parent, child] = field.split('.')
        const parentValue = prev[parent as keyof CourseForm] as any
        const currentArray = parentValue?.[child] || []
        return {
          ...prev,
          [parent]: {
            ...parentValue,
            [child]: (Array.isArray(currentArray) ? currentArray : []).filter((_, i) => i !== index)
          }
        }
      }
      // Handle direct array fields
      const currentArray = prev[field as keyof CourseForm] as any[]
      return {
        ...prev,
        [field]: (Array.isArray(currentArray) ? currentArray : []).filter((_, i) => i !== index)
      }
    })
    triggerSave()
  }

  // Parse "HH:mm" to minutes since midnight; return null if invalid
  const parseTimeToMinutes = (timeStr: string): number | null => {
    if (!timeStr || typeof timeStr !== 'string') return null
    const parts = timeStr.trim().split(':')
    const h = parseInt(parts[0], 10)
    const m = parseInt(parts[1] || '0', 10)
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null
    return h * 60 + m
  }

  const minutesToTime = (minutes: number): string => {
    const capped = Math.max(0, Math.min(minutes, 23 * 60 + 59))
    const h = Math.floor(capped / 60)
    const m = capped % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  const handleTimeSlotAdd = () => {
    const slots = formData.schedule?.timeSlots || []
    let newTimeSlot: { startTime: string; endTime: string; label: string }

    if (slots.length > 0) {
      const lastSlot = slots[slots.length - 1]
      const startMins = parseTimeToMinutes(lastSlot.startTime || '')
      const endMins = parseTimeToMinutes(lastSlot.endTime || '')

      if (startMins != null && endMins != null && endMins > startMins) {
        const durationMins = endMins - startMins
        const newStartMins = endMins
        const newEndMins = newStartMins + durationMins
        newTimeSlot = {
          startTime: minutesToTime(newStartMins),
          endTime: minutesToTime(newEndMins),
          label: ''
        }
      } else {
        newTimeSlot = { startTime: '09:00', endTime: '09:30', label: '' }
      }
    } else {
      newTimeSlot = { startTime: '09:00', endTime: '09:30', label: '' }
    }

    handleArrayAdd('schedule.timeSlots', newTimeSlot)
  }

  const handleRoomAdd = () => {
    const newRoom = { roomId: '', type: 'CUSTOM' as const, capacity: 0, price: 0, bookingAmount: undefined as number | undefined, amenities: [] }
    handleArrayAdd('accommodation.rooms', newRoom)
  }

  /** When accommodation is on: every selected room must have price > 0 and valid booking amount (backend rules). */
  const validateAccommodationPricing = (): string | null => {
    if (!offersAccommodation) return null
    const rooms = formData.accommodation?.rooms || []
    const rowsWithRoom = rooms.filter((r) => r?.roomId && String(r.roomId).trim())
    if (rowsWithRoom.length === 0) {
      return 'Add at least one room type and select it from the list. Then enter the course price and booking amount for each.'
    }
    for (let i = 0; i < rooms.length; i++) {
      const r = rooms[i]
      if (!r?.roomId || !String(r.roomId).trim()) continue
      const roomLabel = schoolRooms?.find((sr) => sr._id === r.roomId)?.name || `Room ${i + 1}`
      const price = Number(r.price)
      if (!Number.isFinite(price) || price <= 0) {
        return `Enter a course price greater than 0 for "${roomLabel}".`
      }
      const rawBooking: unknown = r.bookingAmount
      const bookingAmount =
        rawBooking === undefined ||
        rawBooking === null ||
        (typeof rawBooking === 'string' && rawBooking.trim() === '')
          ? NaN
          : Number(rawBooking)
      if (!Number.isFinite(bookingAmount) || bookingAmount <= 0) {
        return `Enter a booking amount (upfront fee) greater than 0 for "${roomLabel}".`
      }
      if (bookingAmount <= price * 0.1) {
        const minAmount = (price * 0.1 + 0.01).toFixed(2)
        return `Booking amount for "${roomLabel}" must be more than 10% of the room price (over ${minAmount} ${formData.currency}).`
      }
      if (bookingAmount > price) {
        return `Booking amount for "${roomLabel}" cannot exceed the room price (${price.toFixed(2)} ${formData.currency}).`
      }
    }
    return null
  }

  /** When accommodation is off: course fee + upfront booking amount on the course itself. */
  const validateCourseOnlyPricing = (): string | null => {
    if (offersAccommodation) return null
    const price = Number(formData.price)
    if (!Number.isFinite(price) || price <= 0) {
      return 'Enter a course price greater than 0 for this course-only listing.'
    }
    const rawBooking: unknown = formData.bookingAmount
    const bookingAmount =
      rawBooking === undefined ||
      rawBooking === null ||
      (typeof rawBooking === 'string' && rawBooking.trim() === '')
        ? NaN
        : Number(rawBooking)
    if (!Number.isFinite(bookingAmount) || bookingAmount <= 0) {
      return 'Enter a booking amount (upfront fee) greater than 0 for this course.'
    }
    if (bookingAmount <= price * 0.1) {
      const minAmount = (price * 0.1 + 0.01).toFixed(2)
      return `Booking amount must be more than 10% of the course price (over ${minAmount} ${formData.currency}).`
    }
    if (bookingAmount > price) {
      return `Booking amount cannot exceed the course price (${price.toFixed(2)} ${formData.currency}).`
    }
    return null
  }

  const markStepAttempted = (step: number) => {
    setAttemptedSteps((prev) => new Set(prev).add(step))
  }

  const clearStepAttempted = (step: number) => {
    setAttemptedSteps((prev) => {
      if (!prev.has(step)) return prev
      const next = new Set(prev)
      next.delete(step)
      return next
    })
  }

  const courseFormValidationInput = () => ({
    name: formData.name,
    description: formData.description,
    shortDescription: formData.shortDescription,
    duration: formData.duration,
    startDay: formData.startDay,
    dateRangeRows,
    schedule: formData.schedule,
    media: formData.media,
    whoIsThisFor: formData.whoIsThisFor,
    courseHighlights: formData.courseHighlights,
    whatYouWillGain: formData.whatYouWillGain,
  })

  const getStep3ValidationMessages = (): string[] => {
    const msgs: string[] = []
    if (offersAccommodation) {
      if (!schoolRooms?.length) {
        msgs.push('Add at least one room type for your school (Manage room types), then assign it to this course.')
      }
      const accErr = validateAccommodationPricing()
      if (accErr && !msgs.includes(accErr)) msgs.push(accErr)
    } else {
      const courseErr = validateCourseOnlyPricing()
      if (courseErr) msgs.push(courseErr)
    }
    return msgs
  }

  const nextStep = () => {
    markStepAttempted(currentStep)

    if (currentStep === 1) {
      const stepErrors = validateCourseStep1(courseFormValidationInput())
      if (Object.keys(stepErrors).length > 0) {
        toast.error(Object.values(stepErrors)[0], { id: 'step-1-validation' })
        return
      }
    }
    if (currentStep === 2) {
      const stepErrors = validateCourseStep2(courseFormValidationInput())
      if (Object.keys(stepErrors).length > 0) {
        toast.error(Object.values(stepErrors)[0], { id: 'schedule-validation' })
        return
      }
    }
    if (currentStep === 3) {
      const step3Msgs = getStep3ValidationMessages()
      if (step3Msgs.length > 0) {
        toast.error(step3Msgs[0], {
          id: offersAccommodation ? 'booking-validation' : 'course-pricing-validation',
        })
        return
      }
    }
    if (currentStep < steps.length) {
      const next = currentStep + 1
      if (next === 3) {
        clearStepAttempted(3)
      }
      setCurrentStep(next)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      const prev = currentStep - 1
      if (currentStep === 3) {
        clearStepAttempted(3)
      }
      setCurrentStep(prev)
    }
  }

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the form? All unsaved changes will be lost.')) {
      // Clear all session storage
      sessionStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(PENDING_MEDIA_KEY)
      
      // Reset form data to initial state
      setFormData({
        schoolId: selectedSchool?._id || '',
        name: '',
        type: 'TRAINING',
        description: '',
        shortDescription: '',
        price: undefined,
        bookingAmount: undefined,
        currency: 'USD',
        duration: 1,
        maxStudents: undefined,
        minStudents: undefined,
        difficulty: undefined,
        yogaStyles: [],
        schedule: {
          startDate: '',
          endDate: '',
          timeSlots: []
        },
        startDay: 1,
        activeMonths: [],
        inclusions: [],
        requirements: [],
        whoIsThisFor: [],
        courseHighlights: [],
        whatYouWillGain: [],
        curriculum: [],
        faq: [],
        media: {
          images: [],
          videos: []
        },
        accommodation: {
          available: true,
          rooms: [],
          mealPlan: {
            included: false,
            type: 'VEGETARIAN'
          }
        },
        images: [],
        yogaAllianceCertified: false,
        isActive: false,
        isFeatured: false
      })
      
      // Reset other state
      setDateRangeRows([createEmptyDateRangeRow()])
      setCurrentStep(1)
      setPendingMediaFiles([])
      setMediaUrls({ images: [], videos: [] })
      setTextareaValues({}) // Clear all textarea values
      setOffersAccommodation(true)
      setAttemptedSteps(new Set())
      setCustomYogaStyleInput('')
      prevFormDataRef.current = ''
      prevActiveMonthsRef.current = ''
      
      toast.success('Form reset successfully')
    }
  }

  const handleSubmit = async () => {
    // Wait for auth to finish loading
    if (authLoading) {
      toast.error('Please wait while we load your school information...')
      return
    }

    // Frontend validation (aligned with backend)
    const courseErrors = validateCourse({
      ...courseFormValidationInput(),
      inclusions: formData.inclusions,
      requirements: formData.requirements,
      whoIsThisFor: formData.whoIsThisFor,
      courseHighlights: formData.courseHighlights,
      whatYouWillGain: formData.whatYouWillGain,
      curriculum: formData.curriculum,
      instructor: (formData as { instructor?: { name?: string; bio?: string; certifications?: string[] } }).instructor,
      accommodation: formData.accommodation,
    })
    if (Object.keys(courseErrors).length > 0) {
      const errorStep = getCourseValidationStep(courseErrors)
      setAttemptedSteps((prev) => new Set(prev).add(errorStep))
      setCurrentStep(errorStep)
      toast.error(Object.values(courseErrors)[0])
      return
    }

    console.log('handleSubmit called:', {
      selectedSchool: selectedSchool?._id,
      selectedSchoolFull: selectedSchool,
      schools: schools,
      schoolsLength: schools?.length,
      schoolsType: Array.isArray(schools) ? 'array' : typeof schools,
      authLoading,
      user: user?._id,
      userRole: user?.role,
      isEditMode,
      courseId
    })

    // Use schoolId from user object first (already available after login), fallback to selectedSchool
    const schoolIdToUse = (user as any)?.schoolId || (user as any)?.schoolIds?.[0] || (user as any)?.school_id?.[0] || selectedSchool?._id

    console.log('School ID to use:', {
      fromUserSchoolId: (user as any)?.schoolId,
      fromUserSchoolIds: (user as any)?.schoolIds?.[0],
      fromUserSchool_id: (user as any)?.school_id?.[0],
      fromSelectedSchool: selectedSchool?._id,
      finalSchoolId: schoolIdToUse
    })

    if (!schoolIdToUse) {
      console.error('School ID error:', {
        userSchoolId: (user as any)?.schoolId,
        userSchoolIds: (user as any)?.schoolIds,
        userSchool_id: (user as any)?.school_id,
        selectedSchool: selectedSchool?._id,
        schools: schools,
        schoolsLength: schools?.length,
        isEditMode,
        authLoading
      })

      toast.error('No school associated with your account. Please contact support to create or associate a school with your account.')
      navigate('/dashboard')
      return
    }

    const step3Msgs = getStep3ValidationMessages()
    if (step3Msgs.length > 0) {
      setAttemptedSteps((prev) => new Set(prev).add(3))
      setCurrentStep(3)
      toast.error(step3Msgs[0], {
        id: offersAccommodation ? 'booking-validation' : 'course-pricing-validation',
      })
      return
    }

    const dateRangesPayload = normalizeDateRangeRows(dateRangeRows)

    const scheduleData: any = {
      timeSlots: (formData.schedule?.timeSlots || []).map((ts: any) => ({
        label: ts?.label || '',
        startTime: ts?.startTime || '',
        endTime: ts?.endTime || ''
      })),
      dateRanges: dateRangesPayload,
    }

    // Only include startDate/endDate if they exist (for backward compatibility)
    if (formData.schedule?.startDate) {
      scheduleData.startDate = formData.schedule.startDate
    }
    if (formData.schedule?.endDate) {
      scheduleData.endDate = formData.schedule.endDate
    }

    // Clean up undefined values before sending
    const courseData: any = {
      schoolId: schoolIdToUse,
      name: formData.name,
      type: formData.type,
      description: formData.description,
      shortDescription: formData.shortDescription,
      currency: formData.currency,
      duration: formData.duration,
      schedule: scheduleData,
      inclusions: formData.inclusions || [],
      requirements: formData.requirements || [],
      whoIsThisFor: formData.whoIsThisFor || [],
      courseHighlights: formData.courseHighlights || [],
      whatYouWillGain: formData.whatYouWillGain || [],
      curriculum: (formData.curriculum || []).filter(section => section.title.trim().length > 0 && section.items.length > 0),
      faq: (formData.faq || []).filter(item => item.question?.trim() && item.answer?.trim()),
      media: formData.media ? {
        images: (formData.media.images || [])
          .filter(url => url.trim().length > 0)
          .filter(url => !url.startsWith('blob:') && !url.startsWith('http://localhost')),
        videos: (formData.media.videos || [])
          .filter(url => url.trim().length > 0)
          .filter(url => !url.startsWith('blob:') && !url.startsWith('http://localhost'))
      } : { images: [], videos: [] },
      accommodation: formData.accommodation ? {
        ...formData.accommodation,
        available: offersAccommodation,
        rooms: (formData.accommodation.rooms || [])
          .filter((r) => !!r?.roomId)
          .map((r) => ({
            roomId: r.roomId!,
            price: Number(r.price) || 0,
            bookingAmount: Number(r.bookingAmount) || 0,
          }))
      } : undefined,
      images: formData.images || [],
    yogaAllianceCertified: !!formData.yogaAllianceCertified,
      isActive: formData.isActive !== undefined ? formData.isActive : false,
      isFeatured: formData.isFeatured !== undefined ? formData.isFeatured : false,
      startDay: formData.startDay,
    }
    
    // For update, only include fields that should be updated (don't send schoolId)
    if (isEditMode) {
      delete courseData.schoolId
    }

    // Only include optional fields if they have values
    if (!offersAccommodation) {
      courseData.price = Number(formData.price) || 0
      courseData.bookingAmount = Number(formData.bookingAmount) || 0
    }
    if (formData.maxStudents !== undefined && formData.maxStudents !== null) {
      courseData.maxStudents = formData.maxStudents
    }
    if (formData.minStudents !== undefined && formData.minStudents !== null) {
      courseData.minStudents = formData.minStudents
    }
    if (formData.difficulty !== undefined && formData.difficulty !== null) {
      courseData.difficulty = formData.difficulty
    }
    if (formData.yogaStyles && formData.yogaStyles.length > 0) {
      courseData.yogaStyles = formData.yogaStyles.filter(style => style.trim().length > 0)
    }

    console.log('Course data to submit:', {
      schoolId: courseData.schoolId,
      name: courseData.name,
      type: courseData.type,
      isEditMode,
      courseId,
      courseDataKeys: Object.keys(courseData)
    })

    setIsSubmitting(true)
    try {
      if (isEditMode && courseId) {
        console.log('Updating course:', courseId)
        await coursesService.updateCourse(courseId, courseData as any)
        toast.success('Course updated successfully!')
        navigate(`/courses/${courseId}`)
      } else {
        // New course: media was already uploaded at selection time; courseData.media has file keys from formData.media
        const response = await coursesService.createCourse(courseData as any)
        const newCourseId = response.data?._id
        if (newCourseId) {
          sessionStorage.removeItem(STORAGE_KEY)
          sessionStorage.removeItem(PENDING_MEDIA_KEY)
          setPendingMediaFiles([])
        }
        toast.success('Course created successfully!')
        navigate('/courses')
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error?.message ?? error.response?.data?.error?.details ?? error.message
      toast.error(errMsg || `Failed to ${isEditMode ? 'update' : 'create'} course`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDateRangeRowsChange = (rows: CourseDateRangeRow[]) => {
    setDateRangeRows(rows)
    triggerSave()
  }

  // Helper function to handle textarea changes for array fields (split by newlines)
  // Handle multiple course media uploads (images or videos)
  const handleMultipleMediaUpload = async (files: File[], type: 'image' | 'video') => {
    if (!files.length) return
    if (!formData.schoolId) {
      toast.error('Please select a school first')
      return
    }

    const uploadedImages = (formData.media?.images || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')
    ).length
    const uploadedVideos = (formData.media?.videos || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')
    ).length
    const pendingImages = pendingMediaFiles.filter((p) => p.type === 'image' && p.category === 'media').length
    const pendingVideos = pendingMediaFiles.filter((p) => p.type === 'video' && p.category === 'media').length
    const currentImages = uploadedImages + pendingImages
    const currentVideos = uploadedVideos + pendingVideos

    if (type === 'image') {
      const validation = validateImageFiles(files, 'courseMedia', currentImages)
      if (!validation.valid) {
        toast.error(validation.error || 'Validation failed')
        return
      }
    } else {
      const validation = validateVideoFiles(files, 'courseMedia', currentVideos)
      if (!validation.valid) {
        toast.error(validation.error || 'Validation failed')
        return
      }
    }

    const maxImages = MEDIA_SECTIONS.courseMedia.maxImages ?? 5
    const maxVideos = MEDIA_SECTIONS.courseMedia.maxVideos ?? 2
    const toAdd = type === 'image'
      ? files.slice(0, Math.max(0, maxImages - currentImages))
      : files.slice(0, Math.max(0, maxVideos - currentVideos))
    if (toAdd.length === 0) return

    // Upload to Cloudflare immediately (whether course exists or not)
    setUploadingMedia(true)
    const fileKeys: string[] = []
    try {
      for (const file of toAdd) {
        const result = type === 'image'
          ? await coursesService.uploadCourseImage(file, formData.schoolId, courseId)
          : await coursesService.uploadCourseVideo(file, formData.schoolId, courseId)
        if (result.success && result.data?.fileKey) {
          fileKeys.push(result.data.fileKey)
        } else {
          toast.error(`Failed to upload ${file.name}`)
        }
      }
      if (fileKeys.length > 0) {
        const currentMedia = formData.media || { images: [], videos: [] }
        const cleanImages = (currentMedia.images || []).filter(
          (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')
        )
        const cleanVideos = (currentMedia.videos || []).filter(
          (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')
        )
        const newMedia = {
          images: type === 'image' ? [...cleanImages, ...fileKeys] : cleanImages,
          videos: type === 'video' ? [...cleanVideos, ...fileKeys] : cleanVideos,
        }
        handleInputChange('media', newMedia)
        if (courseId) {
          await coursesService.updateCourse(courseId, { media: newMedia } as any)
        }
        toast.success(`${fileKeys.length} ${type === 'image' ? 'image(s)' : 'video(s)'} uploaded to cloud`)
      }
    } catch (error: any) {
      toast.error(error.message || `Failed to upload ${type}s`)
    } finally {
      setUploadingMedia(false)
    }
  }

  // Handle single course media upload (delegates to multiple for consistency)
  const handleMediaUpload = async (file: File, type: 'image' | 'video') => {
    await handleMultipleMediaUpload([file], type)
  }

  // Remove course image at display index (handles both uploaded and pending)
  const handleRemoveCourseImage = (displayIndex: number) => {
    const validImageKeys = (formData.media?.images || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')
    )
    const pendingImageItems = pendingMediaFiles.filter(
      (p) => p.type === 'image' && p.category === 'media' && p.previewUrl && p.uploadStatus !== 'failed'
    )
    if (displayIndex < validImageKeys.length) {
      const newImages = validImageKeys.filter((_, i) => i !== displayIndex)
      const newMedia = { ...formData.media, images: newImages, videos: formData.media?.videos || [] }
      handleInputChange('media', newMedia)
      if (courseId) {
        coursesService.updateCourse(courseId, { media: newMedia } as any).catch(() => {})
      }
    } else {
      const pendingIndex = displayIndex - validImageKeys.length
      if (pendingIndex >= 0 && pendingIndex < pendingImageItems.length) {
        const toRemove = pendingImageItems[pendingIndex]
        if (toRemove.previewUrl) URL.revokeObjectURL(toRemove.previewUrl)
        setPendingMediaFiles((prev) => {
          const updated = prev.filter((p) => p !== toRemove)
          savePendingMediaToStorage(updated)
          return updated
        })
      }
    }
  }

  // Remove course video at display index (handles both uploaded and pending)
  const handleRemoveCourseVideo = (displayIndex: number) => {
    const validVideoKeys = (formData.media?.videos || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')
    )
    const pendingVideoItems = pendingMediaFiles.filter(
      (p) => p.type === 'video' && p.category === 'media' && p.previewUrl && p.uploadStatus !== 'failed'
    )
    if (displayIndex < validVideoKeys.length) {
      const newVideos = validVideoKeys.filter((_, i) => i !== displayIndex)
      const newMedia = { ...formData.media, images: formData.media?.images || [], videos: newVideos }
      handleInputChange('media', newMedia)
      if (courseId) {
        coursesService.updateCourse(courseId, { media: newMedia } as any).catch(() => {})
      }
    } else {
      const pendingIndex = displayIndex - validVideoKeys.length
      if (pendingIndex >= 0 && pendingIndex < pendingVideoItems.length) {
        const toRemove = pendingVideoItems[pendingIndex]
        if (toRemove.previewUrl) URL.revokeObjectURL(toRemove.previewUrl)
        setPendingMediaFiles((prev) => {
          const updated = prev.filter((p) => p !== toRemove)
          savePendingMediaToStorage(updated)
          return updated
        })
      }
    }
  }

  const handleTextareaArrayChange = (field: string, value: string) => {
    // Store the raw value to preserve newlines while typing
    setTextareaValues(prev => ({ ...prev, [field]: value }))
    // Process into array for storage (filter empty items)
    const items = value.split('\n').map(item => item.trim()).filter(item => item.length > 0)
    handleInputChange(field, items)
  }

  // Helper function to get textarea value (use raw value if available, otherwise from array)
  const getTextareaValue = (field: string, arr: string[] | undefined): string => {
    // If we have a raw value stored, use it (preserves newlines while typing)
    if (textareaValues[field] !== undefined) {
      return textareaValues[field]
    }
    // Otherwise, convert array to string
    return arr && arr.length > 0 ? arr.join('\n') : ''
  }
  
  // Initialize textarea values from formData when formData changes (for edit mode or loading)
  useEffect(() => {
    if ((formData.requirements?.length ?? 0) > 0 && !textareaValues['requirements']) {
      setTextareaValues(prev => ({ ...prev, requirements: (formData.requirements ?? []).join('\n') }))
    }
    if ((formData.whoIsThisFor?.length ?? 0) > 0 && !textareaValues['whoIsThisFor']) {
      setTextareaValues(prev => ({ ...prev, whoIsThisFor: (formData.whoIsThisFor ?? []).join('\n') }))
    }
    if ((formData.courseHighlights?.length ?? 0) > 0 && !textareaValues['courseHighlights']) {
      setTextareaValues(prev => ({ ...prev, courseHighlights: (formData.courseHighlights ?? []).join('\n') }))
    }
    if ((formData.whatYouWillGain?.length ?? 0) > 0 && !textareaValues['whatYouWillGain']) {
      setTextareaValues(prev => ({ ...prev, whatYouWillGain: (formData.whatYouWillGain ?? []).join('\n') }))
    }
    // Initialize curriculum textarea values
    if (formData.curriculum && formData.curriculum.length > 0) {
      formData.curriculum.forEach((section, index) => {
        const key = `curriculum_${index}`
        if (section.items.length > 0 && !textareaValues[key]) {
          setTextareaValues(prev => ({ ...prev, [key]: section.items.join('\n') }))
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.requirements, formData.whoIsThisFor, formData.courseHighlights, formData.whatYouWillGain, formData.curriculum])

  if (authLoading || (isEditMode && loadingCourse)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isEditMode && loadingCourse ? 'Loading course data...' : 'Loading school information...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {isEditMode ? '✏️ Edit Course' : '➕ Create New Course'}
              </h1>
              <p className="text-white/90 text-sm">
                {isEditMode ? 'Update course information and settings' : 'Fill in the details to create a new course'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex gap-6">

          {/* Main Content */}
          <div className="flex-1 relative">
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Step Header */}
              <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 px-4 py-3">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                      {React.createElement(steps[currentStep - 1].icon, { size: 20, className: "text-white" })}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">
                        {steps[currentStep - 1].title}
                      </h2>
                      <p className="text-white/90 text-sm">
                        {steps[currentStep - 1].description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-white">Step {currentStep}</div>
                      <div className="text-white/80 text-xs">of {steps.length}</div>
                    </div>
                    {!isEditMode && (
                      <button
                        type="button"
                        onClick={handleReset}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl"
                        title="Reset form and clear all data"
                      >
                        <RotateCcw size={16} />
                        <span>Reset</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="space-y-8"
                  >
                    {/* Step 1: Basic Info */}
                    {currentStep === 1 && (
                      <div className="space-y-8">
                        {/* Course Name and Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Course Name *
                            </label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                              placeholder="e.g., 200-Hour Yoga Teacher Training"
                              required
                            />
                          </div>

                          <div className="space-y-3">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Course Type *
                            </label>
                            <select
                              value={formData.type}
                              onChange={(e) => handleInputChange('type', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                              required
                            >
                              <option value="TRAINING">Yoga Teacher Training</option>
                              <option value="RETREAT">Yoga Retreat</option>
                            </select>
                          </div>
                        </div>

                        {/* Duration and Difficulty */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Duration (Days) *
                            </label>
                            <input
                              type="number"
                              value={formData.duration}
                              onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                              onFocus={(e) => e.target.select()}
                              onWheel={(e) => (e.target as HTMLInputElement).blur()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                              min="1"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Difficulty Level
                            </label>
                            <select
                              value={formData.difficulty || ''}
                              onChange={(e) => handleInputChange('difficulty', e.target.value || undefined)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                            >
                              <option value="">Select Difficulty (Optional)</option>
                              <option value="ALL_LEVELS">All Levels</option>
                              <option value="BEGINNER">Beginner</option>
                              <option value="INTERMEDIATE">Intermediate</option>
                              <option value="ADVANCED">Advanced</option>
                            </select>
                          </div>
                        </div>

                        {/* Currency and Max Students */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Currency
                            </label>
                            <select
                              value={formData.currency}
                              onChange={(e) => handleInputChange('currency', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="INR">INR</option>
                              <option value="IDR">IDR</option>
                            </select>
                            <p className="text-sm text-gray-500 mt-1">
                              Currency for course and accommodation pricing
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Max Students
                            </label>
                            <input
                              type="number"
                              value={formData.maxStudents || ''}
                              onChange={(e) => handleInputChange('maxStudents', e.target.value ? parseInt(e.target.value) : undefined)}
                              onWheel={(e) => (e.target as HTMLInputElement).blur()}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              min="1"
                              placeholder="Optional"
                            />
                          </div>
                        </div>

                        {/* Yoga Styles */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Yoga Styles
                          </label>
                          <p className="text-sm text-gray-600 mb-3">
                            Select the yoga styles taught in this course (you can select multiple)
                          </p>
                          <div className="space-y-3">
                            {/* Checkbox grid for yoga styles */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                              {PRESET_YOGA_STYLES.map((style) => (
                                <label
                                  key={style}
                                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.yogaStyles?.includes(style) || false}
                                    onChange={(e) => {
                                      const currentStyles = formData.yogaStyles || []
                                      if (e.target.checked) {
                                        handleInputChange('yogaStyles', [...currentStyles, style])
                                      } else {
                                        handleInputChange('yogaStyles', currentStyles.filter(s => s !== style))
                                      }
                                    }}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                  />
                                  <span className="text-sm text-gray-700">{style}</span>
                                </label>
                              ))}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input
                                type="text"
                                value={customYogaStyleInput}
                                onChange={(e) => setCustomYogaStyleInput(e.target.value)}
                                onKeyDown={handleCustomYogaStyleKeyDown}
                                onBlur={() => {
                                  if (customYogaStyleInput.includes(',')) {
                                    addCustomYogaStylesFromInput()
                                  }
                                }}
                                placeholder="Not in the list? Enter style here (comma-separated)"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                              />
                              <button
                                type="button"
                                onClick={addCustomYogaStylesFromInput}
                                disabled={!customYogaStyleInput.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                Add style
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              Press Enter or use commas to add multiple custom styles (e.g. Yin Yoga, Restorative Yoga).
                            </p>
                            {/* Display selected styles as chips */}
                            {formData.yogaStyles && formData.yogaStyles.length > 0 && (
                              <div>
                                <p className="text-xs text-gray-600 mb-2">Selected styles:</p>
                                <div className="flex flex-wrap gap-2">
                                  {formData.yogaStyles.map((style, index) => (
                                    <span
                                      key={`${style}-${index}`}
                                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                                    >
                                      {style}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newStyles = formData.yogaStyles?.filter((_, i) => i !== index) || []
                                          handleInputChange('yogaStyles', newStyles)
                                        }}
                                        className="ml-1 text-primary-600 hover:text-primary-800"
                                      >
                                        <X size={14} />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Yoga Alliance Certified */}
                        <div className="flex items-start justify-between p-4 border border-gray-200 rounded-xl bg-gray-50">
                          <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">
                              Yoga Alliance Certified
                            </label>
                            <p className="text-sm text-gray-600">
                              Mark this course as recognized by Yoga Alliance.
                            </p>
                          </div>
                          <div className="pt-1">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!formData.yogaAllianceCertified}
                                onChange={(e) => handleInputChange('yogaAllianceCertified', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 relative" />
                            </label>
                          </div>
                        </div>

                        {/* Description Section */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Short Description
                            </label>
                            <textarea
                              value={formData.shortDescription || ''}
                              onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              rows={3}
                              placeholder="Brief description (max 200 characters)"
                              maxLength={200}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              {(formData.shortDescription || '').length}/200 characters
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Full Description *
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              rows={4}
                              placeholder="Detailed course description"
                              maxLength={700}
                              required
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              {(formData.description || '').length}/700 characters
                            </div>
                          </div>
                        </div>

                        {/* Inclusions and Requirements */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              What's Included
                            </label>
                            <div className="space-y-2">
                              {formData.inclusions?.map((inclusion, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={inclusion}
                                    onChange={(e) => {
                                      const newInclusions = [...(formData.inclusions || [])]
                                      newInclusions[index] = e.target.value
                                      handleInputChange('inclusions', newInclusions)
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="e.g., Course materials, certificate"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleArrayRemove('inclusions', index)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => handleArrayAdd('inclusions', '')}
                                className="flex items-center text-primary-600 hover:text-primary-700 text-sm"
                              >
                                <span className="mr-1">+</span>
                                Add Inclusion
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Requirements
                            </label>
                            <textarea
                              value={getTextareaValue('requirements', formData.requirements)}
                              onChange={(e) => handleTextareaArrayChange('requirements', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              rows={4}
                              placeholder="Enter each requirement on a new line. For example:&#10;Basic yoga experience&#10;Medical clearance&#10;Minimum age 18"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter each requirement on a new line. Press Enter to add a new requirement.
                            </p>
                          </div>
                        </div>

                        {/* Who Is This For Section */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Who Is This For? *
                          </label>
                          <textarea
                            value={getTextareaValue('whoIsThisFor', formData.whoIsThisFor)}
                            onChange={(e) => handleTextareaArrayChange('whoIsThisFor', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            rows={4}
                            placeholder="Enter each target audience on a new line. For example:&#10;Yoga practitioners seeking to deepen their practice&#10;Aspiring yoga teachers ready to share their passion&#10;Those looking for personal transformation and growth"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter each target audience on a new line. Press Enter to add a new item.
                          </p>
                        </div>

                        {/* Course Highlights Section */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Course Highlights *
                          </label>
                          <textarea
                            value={getTextareaValue('courseHighlights', formData.courseHighlights)}
                            onChange={(e) => handleTextareaArrayChange('courseHighlights', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            rows={4}
                            placeholder="Enter each highlight on a new line. For example:&#10;Yoga Alliance Accredited&#10;Experienced Teachers&#10;Deepen Your Practice&#10;Small Class Sizes"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter each highlight on a new line. Press Enter to add a new highlight.
                          </p>
                        </div>

                        {/* What You Will Gain Section */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            What You Will Gain *
                          </label>
                          <textarea
                            value={getTextareaValue('whatYouWillGain', formData.whatYouWillGain)}
                            onChange={(e) => handleTextareaArrayChange('whatYouWillGain', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            rows={4}
                            placeholder="Enter each benefit on a new line. For example:&#10;The tools to teach confidently&#10;Cultivate a deeper practice&#10;Recognized certification&#10;Comprehensive knowledge"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter each benefit on a new line. Press Enter to add a new benefit.
                          </p>
                        </div>

                        {/* Detailed Curriculum Section */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Detailed Curriculum
                          </label>
                          <div className="space-y-4">
                            {formData.curriculum?.map((section, sectionIndex) => (
                              <div key={sectionIndex} className="border border-gray-200 rounded-md p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                  <input
                                    type="text"
                                    value={section.title}
                                    onChange={(e) => {
                                      const newCurriculum = [...(formData.curriculum || [])]
                                      newCurriculum[sectionIndex].title = e.target.value
                                      handleInputChange('curriculum', newCurriculum)
                                    }}
                                    placeholder="Section Title (e.g., Asana Practice, Philosophy)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 mr-2"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newCurriculum = [...(formData.curriculum || [])]
                                      newCurriculum.splice(sectionIndex, 1)
                                      handleInputChange('curriculum', newCurriculum)
                                    }}
                                    className="text-red-500 hover:text-red-700 p-2"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                                <textarea
                                  value={getTextareaValue(`curriculum_${sectionIndex}`, section.items)}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    // Store raw value
                                    setTextareaValues(prev => ({ ...prev, [`curriculum_${sectionIndex}`]: value }))
                                    // Process into array
                                    const newCurriculum = [...(formData.curriculum || [])]
                                    newCurriculum[sectionIndex].items = value.split('\n').map(item => item.trim()).filter(item => item.length > 0)
                                    handleInputChange('curriculum', newCurriculum)
                                  }}
                                  placeholder="Enter each curriculum item on a new line"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  rows={3}
                                />
                                </div>
                              ))}
                              <button
                                type="button"
                              onClick={() => {
                                const newCurriculum = [...(formData.curriculum || []), { title: '', items: [] }]
                                handleInputChange('curriculum', newCurriculum)
                              }}
                                className="flex items-center text-primary-600 hover:text-primary-700 text-sm"
                              >
                                <span className="mr-1">+</span>
                              Add Curriculum Section
                              </button>
                            </div>
                          </div>

                        {/* FAQ Section */}
                        <div className="border-t border-gray-200 pt-6">
                          <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <HelpCircle className="h-4 w-4 text-primary-600" />
                            Course FAQ
                          </label>
                          <p className="text-xs text-gray-500 mb-3">Add frequently asked questions for this course.</p>
                          <div className="space-y-4">
                            {(formData.faq || []).map((item, faqIdx) => (
                              <div key={faqIdx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                                <div className="flex justify-between items-start gap-2">
                                  <input
                                    type="text"
                                    value={item.question}
                                    onChange={(e) => {
                                      const newFaq = [...(formData.faq || [])]
                                      newFaq[faqIdx] = { ...item, question: e.target.value }
                                      handleInputChange('faq', newFaq)
                                    }}
                                    placeholder="Question"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleInputChange('faq', (formData.faq || []).filter((_, i) => i !== faqIdx))}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                    aria-label="Remove FAQ"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <textarea
                                  value={item.answer}
                                  onChange={(e) => {
                                    const newFaq = [...(formData.faq || [])]
                                    newFaq[faqIdx] = { ...item, answer: e.target.value }
                                    handleInputChange('faq', newFaq)
                                  }}
                                  placeholder="Answer"
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                />
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => handleInputChange('faq', [...(formData.faq || []), { question: '', answer: '' }])}
                              className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                            >
                              <Plus className="h-4 w-4" /> Add FAQ
                            </button>
                          </div>
                        </div>

                        {/* Course Media Section */}
                        <div className="border-t border-gray-200 pt-6">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Course Media
                          </label>
                          {(() => {
                            const uploadedImageCount = (formData.media?.images || []).filter((k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')).length
                            const pendingImageCount = pendingMediaFiles.filter((p) => p.type === 'image' && p.category === 'media').length
                            const totalCourseImages = uploadedImageCount + pendingImageCount
                            const uploadedVideoCount = (formData.media?.videos || []).filter((k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost')).length
                            const pendingVideoCount = pendingMediaFiles.filter((p) => p.type === 'video' && p.category === 'media').length
                            const totalCourseVideos = uploadedVideoCount + pendingVideoCount
                            const maxImages = MEDIA_SECTIONS.courseMedia.maxImages ?? 5
                            const maxVideos = MEDIA_SECTIONS.courseMedia.maxVideos ?? 2
                            return (
                          <>
                          {/* Images */}
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Images (Max 5)
                            </label>
                            <div className="flex flex-wrap gap-3 items-start">
                              {/* Uploaded Images */}
                              {(mediaUrls.images.length > 0 
                                ? mediaUrls.images
                                : (formData.media?.images || []).filter(url => !url.startsWith('blob:') && !url.startsWith('http://localhost'))
                              ).map((imageUrl, index) => (
                                <div key={index} className="relative group">
                                  <img 
                                    src={imageUrl || 'https://via.placeholder.com/96'} 
                                    alt={`Course image ${index + 1}`}
                                    className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/96'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCourseImage(index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                    disabled={uploadingMedia}
                                  >
                                    <X size={14} />
                                  </button>
                        </div>
                              ))}
                              
                              {/* Upload Button - hide when total (uploaded + pending) reaches max */}
                              {totalCourseImages < maxImages && (
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => imageInputRef.current?.click()}
                                    disabled={uploadingMedia}
                                    className="w-24 h-24 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {uploadingMedia ? (
                                      <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
                                    ) : (
                                      <>
                                        <Upload className="h-5 w-5 text-gray-400" />
                                        <span className="text-xs text-gray-500">Add Image(s)</span>
                                      </>
                                    )}
                                  </button>
                                  <input
                                    ref={imageInputRef}
                                    type="file"
                                    accept={MEDIA_SECTIONS.courseMedia.acceptImages}
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                      const files = e.target.files ? Array.from(e.target.files) : []
                                      if (files.length) handleMultipleMediaUpload(files, 'image')
                                      e.target.value = ''
                                    }}
                                    disabled={uploadingMedia}
                                  />
                                </div>
                              )}
                              {totalCourseImages >= maxImages && (
                                <p className="text-xs text-gray-500 w-full">Maximum {MEDIA_SECTIONS.courseMedia.maxImages} images reached</p>
                              )}
                            </div>
                          </div>

                          {/* Videos */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Videos (Max 2)
                            </label>
                            <div className="flex flex-wrap gap-3 items-start">
                              {/* Uploaded Videos */}
                              {(mediaUrls.videos.length > 0 
                                ? mediaUrls.videos
                                : (formData.media?.videos || []).filter(url => !url.startsWith('blob:') && !url.startsWith('http://localhost'))
                              ).map((videoUrl, index) => (
                                <div key={index} className="relative group w-24 h-24 rounded-lg border-2 border-gray-200 bg-gray-900" title={videoUrl}>
                                  <div className="absolute inset-0 rounded-lg overflow-hidden">
                                    {videoUrl && videoUrl.length > 0 ? (
                                      <video
                                        src={videoUrl}
                                        className="w-full h-full object-cover"
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onError={(e) => {
                                          (e.target as HTMLVideoElement).style.display = 'none'
                                          const fallback = (e.target as HTMLVideoElement).nextElementSibling as HTMLElement
                                          if (fallback) fallback.classList.remove('hidden')
                                        }}
                                      />
                                    ) : null}
                                    <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/40 ${videoUrl && videoUrl.length > 0 ? 'hidden' : ''}`}>
                                      <Video className="h-8 w-8 text-primary" />
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCourseVideo(index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 z-10 shrink-0"
                                    disabled={uploadingMedia}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                              
                              {/* Upload Button - hide when total (uploaded + pending) reaches max */}
                              {totalCourseVideos < maxVideos && (
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => videoInputRef.current?.click()}
                                    disabled={uploadingMedia}
                                    className="w-24 h-24 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {uploadingMedia ? (
                                      <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
                                    ) : (
                                      <>
                                        <Upload className="h-5 w-5 text-gray-400" />
                                        <span className="text-xs text-gray-500">Add Video</span>
                                      </>
                                    )}
                                  </button>
                                  <input
                                    ref={videoInputRef}
                                    type="file"
                                    accept={MEDIA_SECTIONS.courseMedia.acceptVideos}
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0]
                                      if (file) handleMediaUpload(file, 'video')
                                      e.target.value = ''
                                    }}
                                    disabled={uploadingMedia}
                                  />
                                </div>
                              )}
                              {totalCourseVideos >= maxVideos && (
                                <p className="text-xs text-gray-500 w-full">Maximum {MEDIA_SECTIONS.courseMedia.maxVideos} videos reached</p>
                              )}
                            </div>
                          </div>
                          </>
                        )
                      })()}
                        </div>
                      </div>
                    )}

                    {/* Step 2: Schedule */}
                    {currentStep === 2 && (
                      <div className="space-y-6">
                        <CourseDateRangesEditor
                          rows={dateRangeRows}
                          onChange={handleDateRangeRowsChange}
                        />


                        {/* Time Slots */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Daily Schedule <span className="text-red-500">*</span>
                          </label>
                          <div className="space-y-3">
                            {formData.schedule?.timeSlots?.map((slot, index) => (
                              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 border border-gray-200 rounded-md">
                                <input
                                  type="text"
                                  value={slot.label || ''}
                                  onChange={(e) => {
                                    const newTimeSlots = [...(formData.schedule?.timeSlots || [])]
                                    newTimeSlots[index].label = e.target.value
                                    handleNestedChange('schedule', 'timeSlots', newTimeSlots)
                                  }}
                                  placeholder="Label (e.g., Morning Practice) *"
                                  required
                                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <input
                                  type="time"
                                  value={slot.startTime}
                                  onChange={(e) => {
                                    const newTimeSlots = [...(formData.schedule?.timeSlots || [])]
                                    newTimeSlots[index].startTime = e.target.value
                                    handleNestedChange('schedule', 'timeSlots', newTimeSlots)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <input
                                  type="time"
                                  value={slot.endTime}
                                  onChange={(e) => {
                                    const newTimeSlots = [...(formData.schedule?.timeSlots || [])]
                                    newTimeSlots[index].endTime = e.target.value
                                    handleNestedChange('schedule', 'timeSlots', newTimeSlots)
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleArrayRemove('schedule.timeSlots', index)}
                                  className="text-red-500 hover:text-red-700 flex items-center justify-center"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={handleTimeSlotAdd}
                              className="flex items-center text-primary-600 hover:text-primary-700"
                            >
                              <span className="mr-1">+</span>
                              Add Time Slot
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Accommodation */}
                    {currentStep === 3 && (
                      <div className="space-y-6">
                        <p className="text-sm text-gray-600">
                          Choose how students book this course. You can offer accommodation, or list the course only so students arrange their own stay.
                        </p>

                        <div>
                          <p className="text-sm font-medium text-gray-900 mb-3">
                            Do you offer accommodation? <span className="text-red-500">*</span>
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setAccommodationChoice(true)}
                              className={`px-4 py-3 rounded-lg border-2 text-left transition-colors ${
                                offersAccommodation === true
                                  ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-200'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <span className="block text-sm font-semibold text-gray-900">Yes</span>
                              <span className="block text-xs text-gray-600 mt-1">
                                Students can book room types with this course
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setAccommodationChoice(false)}
                              className={`px-4 py-3 rounded-lg border-2 text-left transition-colors ${
                                offersAccommodation === false
                                  ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-200'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <span className="block text-sm font-semibold text-gray-900">No</span>
                              <span className="block text-xs text-gray-600 mt-1">
                                Course only — students arrange their own stay
                              </span>
                            </button>
                          </div>
                        </div>

                        {offersAccommodation === true && (
                          <>
                            {/* Accommodation room types */}
                            <div>
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Room Types
                                </label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const sid = formData.schoolId || selectedSchool?._id
                                    if (!sid) {
                                      toast.error('Select a school first')
                                      return
                                    }
                                    setRoomTypesModalOpen(true)
                                  }}
                                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                                >
                                  Manage room types
                                </button>
                              </div>
                              {loadingRooms ? (
                                <p className="text-sm text-gray-500">Loading rooms...</p>
                              ) : !schoolRooms || schoolRooms.length === 0 ? (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                  <p className="text-sm text-amber-800 font-medium mb-2">
                                    No room types yet. Create accommodation room types first, then add them here.
                                  </p>
                                  <p className="text-sm text-amber-700 mb-3">
                                    Open the window below to add room types. Your course draft stays on this page and the list updates when you are done.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const sid = formData.schoolId || selectedSchool?._id
                                      if (!sid) {
                                        toast.error('Select a school first')
                                        return
                                      }
                                      setRoomTypesModalOpen(true)
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
                                  >
                                    <Home size={18} />
                                    Add room types
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                                    <p className="text-sm text-blue-800">
                                      The <span className="font-semibold">Price</span> you enter below is the
                                      <span className="font-semibold"> course price for that accommodation type</span>.
                                      Students will see this as the course fee for the selected room.
                                    </p>
                                  </div>
                                  {formData.accommodation?.rooms?.map((room, index) => {
                                    const selectedRoom = schoolRooms?.find(r => r._id === room.roomId)
                                    // Room IDs already selected in other rows (same room can't be selected twice)
                                    const selectedElsewhere = (formData.accommodation?.rooms || [])
                                      .map((r, i) => (i !== index && r.roomId) ? r.roomId : null)
                                      .filter(Boolean) as string[]
                                    const availableRooms = schoolRooms?.filter(
                                      (r) => r._id === room.roomId || !selectedElsewhere.includes(r._id)
                                    ) ?? []
                                    return (
                                      <div key={index} className="p-4 border border-gray-200 rounded-md space-y-3">
                                        <div className="flex items-start gap-3">
                                          <select
                                            value={room.roomId || ''}
                                            onChange={(e) => {
                                              const selectedRoomId = e.target.value
                                              const selectedRoomData = schoolRooms?.find(r => r._id === selectedRoomId)
                                              if (selectedRoomData) {
                                                const newRooms = [...(formData.accommodation?.rooms || [])]
                                                newRooms[index] = {
                                                  roomId: selectedRoomData._id,
                                                  type: selectedRoomData.type,
                                                  capacity: selectedRoomData.capacity,
                                                  price: newRooms[index]?.price ?? 0,
                                                  bookingAmount: newRooms[index]?.bookingAmount,
                                                  amenities: []
                                                }
                                                handleNestedChange('accommodation', 'rooms', newRooms)
                                              }
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                                            required
                                          >
                                            <option value="">Select a room type</option>
                                            {availableRooms.map((r) => (
                                              <option key={r._id} value={r._id}>
                                                {r.name} ({r.type}) - Capacity: {r.capacity}
                                              </option>
                                            ))}
                                          </select>
                                          <button
                                            type="button"
                                            onClick={() => handleArrayRemove('accommodation.rooms', index)}
                                            className="text-red-500 hover:text-red-700 p-2 flex-shrink-0"
                                            aria-label="Remove room"
                                          >
                                            <X size={16} />
                                          </button>
                                        </div>
                                        {selectedRoom && (
                                          <p className="text-sm text-gray-500">Capacity: {selectedRoom.capacity} | Type: {selectedRoom.type}</p>
                                        )}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                              Course price for this accommodation ({formData.currency})
                                            </label>
                                            <input
                                              type="number"
                                              value={room.price || ''}
                                              onChange={(e) => {
                                                const newRooms = [...(formData.accommodation?.rooms || [])]
                                                newRooms[index].price = parseFloat(e.target.value) || 0
                                                handleNestedChange('accommodation', 'rooms', newRooms)
                                              }}
                                              onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                              placeholder="Room price"
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                                              min="0"
                                              step="0.01"
                                              required
                                            />
                                            <p className="text-xs text-gray-500 mt-0.5">
                                              This is the full course fee for this room type.
                                            </p>
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Booking amount ({formData.currency}) *</label>
                                            <input
                                              type="number"
                                              value={room.bookingAmount ?? ''}
                                              onChange={(e) => {
                                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value)
                                                const newRooms = [...(formData.accommodation?.rooms || [])]
                                                newRooms[index].bookingAmount = val ?? undefined
                                                handleNestedChange('accommodation', 'rooms', newRooms)
                                              }}
                                              onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                              onBlur={() => {
                                                const price = Number(room.price) || 0
                                                const bookingAmount = room.bookingAmount != null ? Number(room.bookingAmount) : undefined
                                                if (price > 0 && bookingAmount != null && bookingAmount > 0) {
                                                  if (bookingAmount <= price * 0.1) {
                                                    const minAmount = (price * 0.1 + 1).toFixed(2)
                                                    toast.error(`Booking amount must be more than 10% of course fee (min ${minAmount} ${formData.currency}) to cover platform commission (10% of course fee)`, { id: 'booking-validation' })
                                                  } else if (bookingAmount > price) {
                                                    toast.error(`Booking amount cannot exceed the room price (${price.toFixed(2)} ${formData.currency})`, { id: 'booking-validation' })
                                                  }
                                                }
                                              }}
                                              placeholder="Booking amount (required)"
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                                              min="0"
                                              step="0.01"
                                              required
                                            />
                                            <p className="text-xs text-gray-500 mt-0.5">
                                              Must be more than 10% of the course fee (platform commission is 10% of course fee, deducted from this amount) and cannot exceed the course fee
                                            </p>
                                          </div>
                                        </div>
                                        {selectedRoom?.notes && (
                                          <p className="text-xs text-gray-500 italic">{selectedRoom.notes}</p>
                                        )}
                                        <p className="text-xs text-gray-500">Room details (type, capacity, images) are managed in Accommodation. Here you set price and booking amount for this course.</p>
                                      </div>
                                    )
                                  })}
                                  {(schoolRooms?.length ?? 0) > (formData.accommodation?.rooms?.length ?? 0) && (
                                    <button
                                      type="button"
                                      onClick={handleRoomAdd}
                                      className="flex items-center text-primary-600 hover:text-primary-700 text-sm"
                                    >
                                      <span className="mr-1">+</span>
                                      Add Room Type
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Meal Plan */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Meal Plan
                              </label>
                              <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id="mealPlanIncluded"
                                    checked={formData.accommodation?.mealPlan?.included || false}
                                    onChange={(e) => {
                                      const currentMealPlan = formData.accommodation?.mealPlan || { included: false, type: 'VEGETARIAN' };
                                      handleNestedChange('accommodation', 'mealPlan', {
                                        ...currentMealPlan,
                                        included: e.target.checked
                                      });
                                    }}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                  />
                                  <label htmlFor="mealPlanIncluded" className="text-sm font-medium text-gray-700">
                                    Meals Included
                                  </label>
                                </div>
                                {formData.accommodation?.mealPlan?.included && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Meal Type</label>
                                      <select
                                        value={formData.accommodation?.mealPlan?.type || 'VEGETARIAN'}
                                        onChange={(e) => {
                                          const currentMealPlan = formData.accommodation?.mealPlan || { included: true, type: 'VEGETARIAN' };
                                          handleNestedChange('accommodation', 'mealPlan', {
                                            ...currentMealPlan,
                                            type: e.target.value
                                          });
                                        }}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500"
                                      >
                                        <option value="VEGETARIAN">Vegetarian</option>
                                        <option value="VEGAN">Vegan</option>
                                        <option value="OMNIVORE">Omnivore</option>
                                        <option value="CUSTOM">Custom</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Meal Plan Description</label>
                                      <textarea
                                        value={formData.accommodation?.mealPlan?.description || ''}
                                        onChange={(e) => {
                                          const currentMealPlan = formData.accommodation?.mealPlan || { included: true, type: 'VEGETARIAN' };
                                          handleNestedChange('accommodation', 'mealPlan', {
                                            ...currentMealPlan,
                                            description: e.target.value
                                          });
                                        }}
                                        placeholder="e.g. Three vegetarian meals per day"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 focus:border-primary-500 min-h-[80px] resize-y"
                                        rows={3}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {offersAccommodation === false && (
                          <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">Course only</span> — no accommodation from the school. Students attend the training and arrange their own stay. Set the course fee and upfront booking amount below.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Course price ({formData.currency}) <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={formData.price ?? ''}
                                  onChange={(e) =>
                                    handleInputChange(
                                      'price',
                                      e.target.value === '' ? undefined : parseFloat(e.target.value),
                                    )
                                  }
                                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="Full course fee"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Booking amount — upfront fee ({formData.currency}) <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={formData.bookingAmount ?? ''}
                                  onChange={(e) =>
                                    handleInputChange(
                                      'bookingAmount',
                                      e.target.value === '' ? undefined : parseFloat(e.target.value),
                                    )
                                  }
                                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="Amount paid online when booking"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Must be more than 10% of the course price and cannot exceed the full course price.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 4: Review */}
                    {currentStep === 4 && (
                      <div className="space-y-6">
                        <div className="bg-gray-50 rounded-lg p-6">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Course Summary</h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                              <div className="space-y-1 text-sm text-gray-600">
                                <p><span className="font-medium">Name:</span> {formData.name}</p>
                                <p><span className="font-medium">Type:</span> {formData.type === 'TRAINING' ? 'Yoga Teacher Training' : 'Yoga Retreat'}</p>
                                <p><span className="font-medium">Duration:</span> {formData.duration} days</p>
                                {formData.difficulty && (
                                  <p><span className="font-medium">Difficulty:</span> {formData.difficulty}</p>
                                )}
                                {formData.maxStudents && (
                                  <p><span className="font-medium">Max Students:</span> {formData.maxStudents}</p>
                                )}
                              </div>
                            </div>

                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Schedule</h4>
                              <div className="space-y-1 text-sm text-gray-600">
                                <p><span className="font-medium">Date ranges:</span>{' '}
                                  {normalizeDateRangeRows(dateRangeRows).length > 0
                                    ? formatDateRangesSummary(normalizeDateRangeRows(dateRangeRows))
                                    : 'None added'}
                                </p>
                                <p><span className="font-medium">Time Slots:</span> {formData.schedule?.timeSlots?.length || 0}</p>
                              </div>
                            </div>

                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Accommodation</h4>
                              <div className="space-y-1 text-sm text-gray-600">
                                <p><span className="font-medium">Available:</span>{' '}
                                  {offersAccommodation
                                    ? 'Yes (with room options)'
                                    : 'No — course only'}
                                </p>
                                {offersAccommodation === true ? (
                                  <>
                                    <p><span className="font-medium">Room Types:</span> {formData.accommodation?.rooms?.length || 0}</p>
                                    <p><span className="font-medium">Meals Included:</span> {formData.accommodation?.mealPlan?.included ? 'Yes' : 'No'}</p>
                                  </>
                                ) : offersAccommodation === false ? (
                                  <>
                                    <p><span className="font-medium">Course price:</span>{' '}
                                      {formData.price != null && formData.price > 0
                                        ? `${formData.currency} ${Number(formData.price).toLocaleString()}`
                                        : 'Not set'}
                                    </p>
                                    <p><span className="font-medium">Booking amount:</span>{' '}
                                      {formData.bookingAmount != null && formData.bookingAmount > 0
                                        ? `${formData.currency} ${Number(formData.bookingAmount).toLocaleString()}`
                                        : 'Not set'}
                                    </p>
                                  </>
                                ) : null}
                              </div>
                            </div>

                          </div>

                          {formData.description && (
                            <div className="mt-4">
                              <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                              <p className="text-sm text-gray-600">{formData.description}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
                
                {attemptedSteps.has(currentStep) && currentStep < steps.length && (() => {
                  const messages =
                    currentStep === 1
                      ? Object.values(validateCourseStep1(courseFormValidationInput()))
                      : currentStep === 2
                        ? Object.values(validateCourseStep2(courseFormValidationInput()))
                        : getStep3ValidationMessages()
                  if (messages.length === 0) return null
                  return (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <h3 className="text-sm font-medium text-amber-900 mb-2">
                      Please fix the following before continuing:
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                      {messages.map((msg) => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                  )
                })()}
              </div>

              {/* Step Navigation */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Previous
                </button>

                <div className="flex space-x-3">
                  {currentStep === steps.length ? (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex items-center px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <Save size={16} className="mr-2" />
                      {isSubmitting 
                        ? (isEditMode ? 'Updating...' : 'Creating...') 
                        : (isEditMode ? 'Update Course' : 'Create Course')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-all duration-200"
                    >
                      Next
                      <ArrowRight size={16} className="ml-2" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    {(formData.schoolId || selectedSchool?._id) && (
      <RoomTypesManageModal
        isOpen={roomTypesModalOpen}
        onClose={() => setRoomTypesModalOpen(false)}
        schoolId={String(formData.schoolId || selectedSchool?._id || '')}
        schoolName={selectedSchool?.name}
        onRoomsUpdated={() => {
          const sid = formData.schoolId || selectedSchool?._id
          if (sid) fetchSchoolRooms(String(sid))
        }}
      />
    )}
    </>
  )
}

export default CreateCourse
